import { DEFAULT_WORK_LOCATIONS, type InquiryRecruitmentType, type WorkLocations } from 'shared'
import { normalizeQueryAndExclude, quoteIfNeeded, splitQuery } from '@/lib/search-utils'
import { toRecruitmentTypeFormValue } from './recruitment-type-options'

type InquirySearchParams = {
  query?: string | null
  exclude?: string | null
  primaryDivisionId?: number | null
  isMyAssignment?: boolean
  isMyDivision?: boolean
  isNoDeal?: boolean
  salesMemberName?: string | null
  serviceManagerName?: string | null
  inquiryStatusNames?: string[]
  workLocation?: WorkLocations
  recruitmentType?: InquiryRecruitmentType | ''
  startMonth?: string | null
  isStartAsap?: boolean
  estimatedAmountMin?: string | null
  estimatedAmountMax?: string | null
  contractType?: string | null
  assignStatus?: string | null
  assignStartMonth?: string | null
}

/** デフォルトの検索クエリ */
export const DEFAULT_INQUIRY_SEARCH = 'my_division:true'

export class InquirySearchParser {
  private $query: string[] = []
  private $exclude: string[] = []
  primaryDivisionId: number | null = null
  isMyAssignment = false
  isMyDivision = false
  isNoDeal = false
  salesMemberName = ''
  serviceManagerName = ''
  inquiryStatusNames: string[] = []
  workLocation: WorkLocations = { ...DEFAULT_WORK_LOCATIONS }
  recruitmentType: InquiryRecruitmentType | '' = ''
  startMonth = ''
  isStartAsap = false
  estimatedAmountMin = ''
  estimatedAmountMax = ''
  contractType = ''
  assignStatus = ''
  assignStartMonth = ''

  constructor(args?: InquirySearchParams) {
    this.init(args)
  }

  init(args?: InquirySearchParams) {
    this.$query = []
    this.$exclude = []
    this.primaryDivisionId = null
    this.isMyAssignment = false
    this.isMyDivision = false
    this.isNoDeal = false
    this.salesMemberName = ''
    this.serviceManagerName = ''
    this.inquiryStatusNames = []
    this.workLocation = { ...DEFAULT_WORK_LOCATIONS }
    this.recruitmentType = ''
    this.startMonth = ''
    this.isStartAsap = false
    this.estimatedAmountMin = ''
    this.estimatedAmountMax = ''
    this.contractType = ''
    this.assignStatus = ''
    this.assignStartMonth = ''

    if (!args || !Object.keys(args).length) return

    if (args.query) this.query = args.query
    if (args.exclude) this.exclude = args.exclude
    if (args.primaryDivisionId) this.primaryDivisionId = args.primaryDivisionId
    if (args.isMyAssignment) this.isMyAssignment = args.isMyAssignment
    if (args.isMyDivision) this.isMyDivision = args.isMyDivision
    if (args.isNoDeal) this.isNoDeal = args.isNoDeal
    if (args.salesMemberName) this.salesMemberName = args.salesMemberName
    if (args.serviceManagerName) this.serviceManagerName = args.serviceManagerName
    if (args.inquiryStatusNames) this.inquiryStatusNames = args.inquiryStatusNames
    if (args.workLocation) this.workLocation = { ...args.workLocation }
    if (args.recruitmentType) this.recruitmentType = args.recruitmentType
    if (args.startMonth) this.startMonth = args.startMonth
    if (args.isStartAsap) this.isStartAsap = args.isStartAsap
    if (args.estimatedAmountMin) this.estimatedAmountMin = args.estimatedAmountMin
    if (args.estimatedAmountMax) this.estimatedAmountMax = args.estimatedAmountMax
    if (args.contractType) this.contractType = args.contractType
    if (args.assignStatus) this.assignStatus = args.assignStatus
    if (args.assignStartMonth) this.assignStartMonth = args.assignStartMonth

    this.normalize()
  }

  set query(q: string | null) {
    if (q) {
      this.$query = splitQuery(q, { unquote: true })
    }
  }

  get queries(): string[] {
    return this.$query
  }

  get query(): string {
    return this.$query.map(quoteIfNeeded).join(' ')
  }

  set exclude(q: string | null) {
    if (q) {
      this.$exclude = splitQuery(q, { unquote: true })
    }
  }

  get excludes(): string[] {
    return this.$exclude
  }

  get exclude(): string {
    return this.$exclude.map(quoteIfNeeded).join(' ')
  }

  enabled(): boolean {
    return !!(
      this.$query.length ||
      this.$exclude.length ||
      this.primaryDivisionId ||
      this.isMyAssignment ||
      this.isMyDivision ||
      this.isNoDeal ||
      this.salesMemberName ||
      this.serviceManagerName ||
      this.inquiryStatusNames.length ||
      this.workLocation.onsite ||
      this.workLocation.offsite ||
      this.workLocation.remote ||
      this.recruitmentType ||
      this.startMonth ||
      this.isStartAsap ||
      this.estimatedAmountMin ||
      this.estimatedAmountMax ||
      this.contractType ||
      this.assignStatus ||
      this.assignStartMonth
    )
  }

  normalize() {
    const result = normalizeQueryAndExclude(this.$query, this.$exclude)
    this.$query = result.queries
    this.$exclude = result.excludes
  }

  stringify(): string {
    this.normalize()

    const results: string[] = []

    for (const v of this.$query) {
      results.push(quoteIfNeeded(v))
    }
    for (const v of this.$exclude) {
      results.push(`-${quoteIfNeeded(v)}`)
    }

    if (this.primaryDivisionId) results.push(`primary_division:${this.primaryDivisionId}`)
    if (this.isMyAssignment) results.push('my_assignment:true')
    if (this.isMyDivision) results.push('my_division:true')
    if (this.isNoDeal) results.push('no_deal:true')
    if (this.salesMemberName) results.push(`sales_member:${this.salesMemberName}`)
    if (this.serviceManagerName) results.push(`service_manager:${this.serviceManagerName}`)

    for (const name of this.inquiryStatusNames) {
      results.push(`inquiry_status:${name}`)
    }

    if (this.workLocation.onsite) results.push('work_location:onsite')
    if (this.workLocation.offsite) results.push('work_location:offsite')
    if (this.workLocation.remote) results.push('work_location:remote')
    if (this.recruitmentType) results.push(`recruitment_type:${this.recruitmentType}`)
    if (this.startMonth) results.push(`start_month:${this.startMonth}`)
    if (this.isStartAsap) results.push('start_asap:true')
    if (this.estimatedAmountMin) results.push(`amount_min:${this.estimatedAmountMin}`)
    if (this.estimatedAmountMax) results.push(`amount_max:${this.estimatedAmountMax}`)
    if (this.contractType) results.push(`contract_type:${this.contractType}`)
    if (this.assignStatus) results.push(`assign_status:${this.assignStatus}`)
    if (this.assignStartMonth) results.push(`assign_start_month:${this.assignStartMonth}`)

    return results.join(' ')
  }

  parse(queryString: string): this {
    this.init()
    if (!queryString) return this

    const queries = splitQuery(queryString, { unquote: true })

    for (const v of queries) {
      if (v.startsWith('primary_division:')) {
        const value = v.split(':')[1]
        if (value) this.primaryDivisionId = Number(value)
        continue
      }

      if (v === 'my_assignment:true') {
        this.isMyAssignment = true
        continue
      }

      if (v === 'my_division:true') {
        this.isMyDivision = true
        continue
      }

      if (v === 'no_deal:true') {
        this.isNoDeal = true
        continue
      }

      if (v.startsWith('sales_member:')) {
        const value = v.split(':')[1]
        if (value) this.salesMemberName = value
        continue
      }

      if (v.startsWith('service_manager:')) {
        const value = v.split(':')[1]
        if (value) this.serviceManagerName = value
        continue
      }

      if (v.startsWith('inquiry_status:')) {
        const value = v.split(':')[1]
        if (value) this.inquiryStatusNames.push(value)
        continue
      }

      if (v.startsWith('work_location:')) {
        const value = v.split(':')[1]
        if (value === 'onsite') this.workLocation.onsite = true
        if (value === 'offsite') this.workLocation.offsite = true
        if (value === 'remote') this.workLocation.remote = true
        continue
      }

      if (v.startsWith('recruitment_type:')) {
        this.recruitmentType = toRecruitmentTypeFormValue(v.split(':')[1] ?? '')
        continue
      }

      if (v.startsWith('start_month:')) {
        const value = v.split(':')[1]
        if (value) this.startMonth = value
        continue
      }

      if (v === 'start_asap:true') {
        this.isStartAsap = true
        continue
      }

      if (v.startsWith('amount_min:')) {
        const value = v.split(':')[1]
        if (value) this.estimatedAmountMin = value
        continue
      }

      if (v.startsWith('amount_max:')) {
        const value = v.split(':')[1]
        if (value) this.estimatedAmountMax = value
        continue
      }

      if (v.startsWith('contract_type:')) {
        const value = v.split(':')[1]
        if (value) this.contractType = value
        continue
      }

      if (v.startsWith('assign_status:')) {
        const value = v.split(':')[1]
        if (value) this.assignStatus = value
        continue
      }

      if (v.startsWith('assign_start_month:')) {
        const value = v.split(':')[1]
        if (value) this.assignStartMonth = value
        continue
      }

      this.$query.push(v)
    }

    this.normalize()
    return this
  }
}
